import React, { useState } from 'react';
import { useCtx, useRecurso, useAccion, api } from '../estado.jsx';
import { Modal } from './Comunes.jsx';
import { clp, fechaCorta, DOW, etiquetaVisitas } from '../utiles/fechas.js';
import { normTel, formatearTel } from '../utiles/telefono.js';

export function ModalActivo() {
  const { modal } = useCtx();
  if (!modal) return null;
  switch (modal.tipo) {
    case 'reserva':       return <ModalReserva sede={modal.sede} hora={modal.hora} />;
    case 'detalle':       return <ModalDetalle reserva={modal.reserva} hora={modal.hora} />;
    case 'editarCliente': return <ModalEditarCliente cliente={modal.cliente} />;
    case 'venderBono':    return <ModalVenderBono cliente={modal.cliente} />;
    case 'bloqueo':       return <ModalBloqueo datos={modal.datos} />;
    case 'noEliminable':  return <ModalNoEliminable datos={modal.datos} />;
    default:              return null;
  }
}

/* ---------- Reservar un hueco ---------- */

function ModalReserva({ sede, hora }) {
  const { boardDate, closeModal } = useCtx();
  const { ejecutar, enCurso, error, setError } = useAccion();
  const [telefono, setTelefono] = useState('');
  const [nombre, setNombre] = useState('');
  const [bonoClienteId, setBonoClienteId] = useState('');

  // Se buscan los clientes de la sede para reconocer al que ya existe por su
  // teléfono y ofrecer sus bonos. El alta, si no existe, la hace el backend
  // dentro de la misma transacción que la reserva.
  const clientes = useRecurso(() => api.clientes(), []);
  // Se comparan los dos normalizados: el operador escribe el número como se
  // lo dictan y la base lo guarda en E.164.
  const telNormalizado = normTel(telefono);
  const encontrado = telNormalizado
    ? (clientes.datos || []).find((c) => normTel(c.telefono) === telNormalizado)
    : null;
  const bonosUtiles = encontrado ? (encontrado.bonos || []).filter((b) => b.restantes > 0) : [];

  const reservar = () => {
    if (!telefono.trim() || !(nombre.trim() || encontrado)) {
      setError('Hacen falta teléfono y nombre.');
      return;
    }
    ejecutar(
      () => api.crearReserva({
        fecha: boardDate, hora,
        telefono: telefono.trim(),
        nombre: (encontrado ? encontrado.nombre : nombre).trim(),
        bonoClienteId: bonoClienteId || undefined,
      }),
      closeModal,
    );
  };

  return (
    <Modal>
      <div className="hm-eyebrow">{fechaCorta(boardDate)} · {hora}</div>
      <h3 className="hm-title">Reservar en {sede.comuna}</h3>
      <p className="hm-sub">
        Se busca al cliente por teléfono. Si no existe, se da de alta al reservar.
      </p>

      <div className="field"><label>Teléfono</label>
        <input className="inp" type="tel" value={telefono} autoFocus
               onChange={(e) => { setTelefono(e.target.value); setBonoClienteId(''); }}
               placeholder="+56 9 ..." /></div>

      {encontrado ? (
        <div className="reco show">
          <b>{encontrado.nombre}</b> · {etiquetaVisitas(encontrado.visitas).toLowerCase()}
        </div>
      ) : (
        <div className="field"><label>Nombre</label>
          <input className="inp" value={nombre} onChange={(e) => setNombre(e.target.value)}
                 placeholder="Nombre y apellidos" /></div>
      )}

      {bonosUtiles.length ? (
        <div className="field"><label>Usar un bono</label>
          <select className="inp" value={bonoClienteId} onChange={(e) => setBonoClienteId(e.target.value)}>
            <option value="">No usar bono</option>
            {bonosUtiles.map((b) => (
              <option key={b.id} value={b.id}>{b.nombre} · quedan {b.restantes}</option>
            ))}
          </select></div>
      ) : null}

      <div className="inline-err show">{error}</div>
      <div className="hm-actions">
        <button className="btn ghost" onClick={closeModal}>Cancelar</button>
        <button className="btn" onClick={reservar} disabled={enCurso}>
          {enCurso ? 'Reservando…' : 'Reservar'}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- Liberar o cumplir una reserva ---------- */

function ModalDetalle({ reserva, hora }) {
  const { boardDate, closeModal } = useCtx();
  const { ejecutar, enCurso, error } = useAccion();

  const accion = (nombre) => ejecutar(() => api.cambiarReserva(reserva.id, nombre), closeModal);

  return (
    <Modal>
      <div className="hm-eyebrow">{fechaCorta(boardDate)} · {hora}</div>
      <h3 className="hm-title">{reserva.nombre}</h3>
      <p className="hm-sub">
        {formatearTel(reserva.telefono)} · {etiquetaVisitas(reserva.visitas).toLowerCase()}
        {reserva.bono_cliente_id ? ' · sesión de bono' : ''}
      </p>
      <p className="section-note">
        Cumplir la sesión suma una visita al cliente. Liberarla deja el hueco libre y,
        si venía de un bono, devuelve la sesión al saldo.
      </p>
      <div className="inline-err show">{error}</div>
      <div className="hm-actions" style={{ flexWrap: 'wrap', gap: 8 }}>
        <button className="btn ghost" onClick={closeModal}>Cerrar</button>
        <button className="btn ghost" onClick={() => accion('cancelada')} disabled={enCurso}>Cancelar reserva</button>
        <button className="btn ghost" onClick={() => accion('no_show')} disabled={enCurso}>No se presentó</button>
        <button className="btn" onClick={() => accion('cumplida')} disabled={enCurso}>Marcar cumplida</button>
      </div>
    </Modal>
  );
}

/* ---------- Editar ficha ---------- */

function ModalEditarCliente({ cliente }) {
  const { closeModal } = useCtx();
  const { ejecutar, enCurso, error } = useAccion();
  const [f, setF] = useState({
    nombre: cliente.nombre, telefono: cliente.telefono,
    email: cliente.email || '', tuneId: cliente.tune_id || '',
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <Modal>
      <div className="hm-eyebrow">Ficha de cliente</div>
      <h3 className="hm-title">{cliente.nombre}</h3>
      <div className="field"><label>Nombre</label>
        <input className="inp" value={f.nombre} onChange={set('nombre')} /></div>
      <div className="field"><label>Teléfono</label>
        <input className="inp" type="tel" value={f.telefono} onChange={set('telefono')} /></div>
      <div className="field"><label>Email</label>
        <input className="inp" type="email" value={f.email} onChange={set('email')} /></div>
      <div className="field"><label>ID en el software de TUNE</label>
        <input className="inp" value={f.tuneId} onChange={set('tuneId')} placeholder="TN-1042" /></div>
      <div className="inline-err show">{error}</div>
      <div className="hm-actions">
        <button className="btn ghost" onClick={closeModal}>Cancelar</button>
        <button className="btn" disabled={enCurso}
                onClick={() => ejecutar(() => api.editarCliente(cliente.id, {
                  nombre: f.nombre.trim(), telefono: f.telefono.trim(),
                  email: f.email.trim() || undefined, tuneId: f.tuneId.trim() || undefined,
                }), closeModal)}>
          {enCurso ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- Vender bono, con recurrencia opcional ---------- */

function ModalVenderBono({ cliente }) {
  const { sedeId, closeModal } = useCtx();
  const { ejecutar, enCurso, error, setError } = useAccion();
  const sede = useRecurso(() => api.sede(sedeId), [sedeId]);
  const [bonoId, setBonoId] = useState('');
  const [conRecurrencia, setConRecurrencia] = useState(false);
  const [diaSemana, setDiaSemana] = useState(3);
  const [hora, setHora] = useState('11:00');
  const [resultado, setResultado] = useState(null);

  const vender = () => {
    if (!bonoId) { setError('Elige un bono.'); return; }
    ejecutar(
      () => api.venderBono(cliente.id, {
        bonoId,
        ...(conRecurrencia ? { recurrencia: { diaSemana, hora } } : {}),
      }),
      setResultado,
    );
  };

  if (resultado) {
    return (
      <Modal>
        <div className="hm-eyebrow">Bono vendido</div>
        <h3 className="hm-title">{cliente.nombre}</h3>
        {conRecurrencia ? (
          <p className="hm-sub">
            Se han agendado <b>{resultado.agendadas.length}</b> sesiones.
            {resultado.conflictos.length
              ? <> Quedaron <b>{resultado.conflictos.length}</b> sin agendar por falta de equipo
                  libre; esas sesiones siguen en el saldo del bono.</>
              : null}
          </p>
        ) : <p className="hm-sub">El bono queda disponible en la ficha del cliente.</p>}
        {resultado.conflictos && resultado.conflictos.length ? (
          <div className="section-note">
            Sin agendar: {resultado.conflictos.map((f) => fechaCorta(f)).join(' · ')}
          </div>
        ) : null}
        <div className="hm-actions">
          <button className="btn" onClick={closeModal}>Cerrar</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal>
      <div className="hm-eyebrow">Vender bono</div>
      <h3 className="hm-title">{cliente.nombre}</h3>
      <div className="field"><label>Bono</label>
        <select className="inp" value={bonoId} onChange={(e) => setBonoId(e.target.value)}>
          <option value="">Elige un bono…</option>
          {((sede.datos && sede.datos.bonos) || []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.nombre} · {b.sesiones} sesiones · {clp(b.precio)}
            </option>
          ))}
        </select></div>

      <label className="check">
        <input type="checkbox" checked={conRecurrencia}
               onChange={(e) => setConRecurrencia(e.target.checked)} />
        Agendar las sesiones con una recurrencia semanal
      </label>

      {conRecurrencia ? (
        <div className="frow">
          <div className="field"><label>Día</label>
            <select className="inp" value={diaSemana}
                    onChange={(e) => setDiaSemana(parseInt(e.target.value, 10))}>
              {DOW.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select></div>
          <div className="field"><label>Hora</label>
            <input className="inp" type="time" value={hora}
                   onChange={(e) => setHora(e.target.value)} /></div>
        </div>
      ) : null}

      <p className="section-note">
        Las sesiones se agendan donde haya equipo libre. Las que queden en conflicto no
        consumen saldo y se pueden agendar después a mano.
      </p>
      <div className="inline-err show">{error}</div>
      <div className="hm-actions">
        <button className="btn ghost" onClick={closeModal}>Cancelar</button>
        <button className="btn" onClick={vender} disabled={enCurso}>
          {enCurso ? 'Vendiendo…' : 'Vender bono'}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- El sistema se niega, y explica por qué ---------- */

/**
 * No es un error de formato: es un cambio rechazado porque dejaría gente
 * tirada. Va en modal para que no se pueda pasar por alto, con las horas
 * afectadas sueltas porque hay que ir a buscarlas al tablero.
 */
function ModalBloqueo({ datos }) {
  const { closeModal } = useCtx();
  const n = datos?.reservasFuera || 0;

  return (
    <Modal>
      <div className="hm-eyebrow">Cambio no aplicado</div>
      <h3 className="hm-title">
        El horario {datos?.horario || ''} deja fuera {n} reserva{n > 1 ? 's' : ''}
      </h3>
      <p className="hm-sub">
        {n > 1 ? 'Ya están confirmadas' : 'Ya está confirmada'} a {n > 1 ? 'estas horas' : 'esta hora'}:
      </p>

      <div className="aviso-horas">
        {(datos?.horas || []).map((h) => <span key={h} className="aviso-hora">{h}</span>)}
      </div>

      <p className="section-note">
        Esas personas ya tienen su pase y se presentarían con el centro cerrado.
        <b> Libera esas reservas desde el tablero del día</b> y vuelve a intentarlo.
      </p>

      <div className="hm-actions">
        <button className="btn" onClick={closeModal}>Entendido</button>
      </div>
    </Modal>
  );
}

/**
 * Un centro con historial no se elimina.
 *
 * El modal ofrece la alternativa que el servidor recomienda, en vez de
 * limitarse a nombrarla: quien llega aquí quería quitar ese centro de en
 * medio, y desactivarlo es lo que resuelve su problema sin destruir nada.
 */
function ModalNoEliminable({ datos }) {
  const { closeModal } = useCtx();
  const { ejecutar, enCurso, error } = useAccion();
  const { clientes = 0, reservas = 0, nombre = 'Este centro', sedeId } = datos || {};

  const desactivar = () => ejecutar(() => api.editarSede(sedeId, { activa: false }), closeModal);

  return (
    <Modal>
      <div className="hm-eyebrow">No se puede eliminar</div>
      <h3 className="hm-title">{nombre} tiene historial</h3>
      <p className="hm-sub">Eliminarlo borraría también:</p>

      <div className="aviso-horas">
        {clientes ? <span className="aviso-hora">{clientes} cliente{clientes > 1 ? 's' : ''}</span> : null}
        {reservas ? <span className="aviso-hora">{reservas} reserva{reservas > 1 ? 's' : ''}</span> : null}
        <span className="aviso-hora">sus bonos vendidos</span>
      </div>

      <p className="section-note">
        <b>Desactivarlo</b> lo retira del escaparate y del buscador sin borrar nada,
        y se puede deshacer cuando quieras.
      </p>

      <div className="inline-err show">{error}</div>
      <div className="hm-actions">
        <button className="btn ghost" onClick={closeModal} disabled={enCurso}>Cancelar</button>
        <button className="btn" onClick={desactivar} disabled={enCurso}>
          {enCurso ? 'Desactivando…' : 'Desactivar centro'}
        </button>
      </div>
    </Modal>
  );
}
